/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { Component } from "react";
import { overlayEvents } from "overlays/OverlayEvents";
import { overlayEventNames } from "constants/constants";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import * as stores from "stores";
import { getMessage } from "utils/TranslationUtil";
import RestrictedPermLockIcon from "dc-icons/viewer2021/SDC_LockRestrict_18_N.svg";
import CustomIcon from "dc-preview-utils/src/components/CustomIcon";
import localizeComponent from "dc-preview-utils/src/Utils/LocalizationUtil";
import loadTranslation from "utils/TranslationUtil";
import { actionTags } from "constants/enums";
import { shouldShowDocumentProperties } from "utils/AppUIUtils";
import { isInitialPageLoaded } from "selectors/pagedViewState";
import { rhpDropinIds } from "constants/enums";
import { shouldShowPermissionsInfo } from "utils/DocPermissionsUtils";
import { isFilePasswordProtected } from "selectors/documentSelectors";

class DocumentPropertiesBootstrap extends Component {
  constructor() {
    super();
    this.shouldShowExpandedComponent = false;
    this.localizedComponent = null;
  }

  getLabel = () => getMessage("label.permissionsDetails");

  getIcon = () => () => <CustomIcon url={RestrictedPermLockIcon} />;

  getTooltip = () => getMessage("modernviewer.tooltip.permissionDetails");

  getAriaLabel = () => getMessage("modernviewer.arialabel.permissionDetails");

  getActionTags = () => [actionTags.LOCAL];

  getFileSelectionParams = () => ({
    acceptMimeTypes: ["application/pdf"],
    acceptExtensions: ["pdf"],
    acceptConnectors: "all"
  });

  id = rhpDropinIds.PERMISSION_DETAILS;

  updateShouldShowDocPropertiesDropin = (opts, shouldShowDocProperties) => {
    opts.showCallback(shouldShowDocProperties);
    this.shouldShowExpandedComponent = shouldShowDocProperties;
  };

  _shouldShowDocPropertiesDropin = () => {
    if (
      isInitialPageLoaded(stores) &&
      shouldShowDocumentProperties() &&
      (isFilePasswordProtected(stores) || shouldShowPermissionsInfo())
    ) {
      return true;
    }
    return false;
  };

  initializeAction = opts => {
    overlayEvents.on(overlayEventNames.MULTI_DOC_MODE_CHANGED, value => {
      if (value) this.updateShouldShowDocPropertiesDropin(opts, false);
    });
    subscribeToSelector(stores, isInitialPageLoaded, () => {
      if (this._shouldShowDocPropertiesDropin()) {
        this.updateShouldShowDocPropertiesDropin(opts, true);
      } else {
        this.updateShouldShowDocPropertiesDropin(opts, false);
      }
    });
  };

  _loadPermDetailsComponent = () =>
    new Promise((resolve, reject) => {
      if (this.localizedComponent) {
        return resolve(this.localizedComponent);
      }

      import(
        /* webpackChunkName: "document-permissions" */
        "./PermissionDetailsRHPComponent"
      )
        .then(module => {
          const permissionDetailsComponent = module.default;
          this.localizedComponent = localizeComponent(permissionDetailsComponent, loadTranslation);
          resolve(this.localizedComponent);
        })
        .catch(() => reject(null));
    });

  getExpandedComponent = () =>
    new Promise((resolve, reject) => {
      if (!this.shouldShowExpandedComponent) {
        return reject(null);
      }

      this._loadPermDetailsComponent()
        .then(component => resolve(component))
        .catch(() => reject(null));
    });

  render() {
    return null;
  }
}

export default DocumentPropertiesBootstrap;
